import { Injectable, signal, computed, inject, NgZone } from '@angular/core';
import {
  getAuth,
  linkWithPopup,
  reauthenticateWithPopup,
  onAuthStateChanged,
  GithubAuthProvider,
  User,
} from 'firebase/auth';
import { getFirestore, doc, setDoc, getDoc } from 'firebase/firestore';
import { AppUser } from '../models/user.model';
import { AuthBridgeService } from './auth-bridge.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private zone = inject(NgZone);
  private readonly authBridge = inject(AuthBridgeService);

  private readonly _currentUser = signal<AppUser | null>(null);
  private readonly _firebaseUser = signal<User | null>(null);
  private readonly _isLoading = signal(true);

  readonly currentUser = this._currentUser.asReadonly();
  readonly firebaseUser = this._firebaseUser.asReadonly();
  readonly isLoading = this._isLoading.asReadonly();
  readonly isAuthenticated = computed(() => this._currentUser() !== null);
  readonly isGitHubLinked = computed(() => {
    const user = this._firebaseUser();
    return user?.providerData.some((p) => p.providerId === 'github.com') ?? false;
  });

  constructor() {
    const auth = getAuth();
    onAuthStateChanged(auth, (user) => {
      this.zone.run(async () => {
        this._firebaseUser.set(user);
        if (user) {
          const appUser = await this.ensureUserDocument(user);
          this._currentUser.set(appUser);
        } else {
          this._currentUser.set(null);
        }
        this._isLoading.set(false);
      });
    });
  }

  private githubProvider(): GithubAuthProvider {
    const provider = new GithubAuthProvider();
    provider.addScope('repo');
    // read:org lets the Insights Cloud Functions list the user's orgs and verify
    // org membership (insightsListOrgs / getActivity); repo alone can't see that.
    provider.addScope('read:org');
    return provider;
  }

  async linkGitHub(): Promise<void> {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');

    const result = await linkWithPopup(user, this.githubProvider());
    const credential = GithubAuthProvider.credentialFromResult(result);
    await this.persistGitHubToken(user.uid, credential?.accessToken);
  }

  /**
   * Re-run the GitHub OAuth consent for an ALREADY-linked account. `linkWithPopup`
   * throws `auth/provider-already-linked` once linked, so this uses
   * `reauthenticateWithPopup` instead — it re-shows GitHub's authorization page
   * (where the user can grant access to an organization they previously skipped)
   * and returns a fresh access token, which we re-persist for the Cloud Functions.
   */
  async reconnectGitHub(): Promise<void> {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');

    const result = await reauthenticateWithPopup(user, this.githubProvider());
    const credential = GithubAuthProvider.credentialFromResult(result);
    await this.persistGitHubToken(user.uid, credential?.accessToken);
  }

  /** Fetch the GitHub username and persist the token doc used by the Functions. */
  private async persistGitHubToken(uid: string, accessToken: string | undefined): Promise<void> {
    if (!accessToken) return;
    let username = '';
    try {
      const res = await fetch('https://api.github.com/user', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (res.ok) {
        username = (await res.json()).login || '';
      }
    } catch {}

    const db = getFirestore();
    await setDoc(doc(db, 'tokens', uid, 'providers', 'github'), {
      accessToken,
      username,
      linkedAt: new Date(),
    });
  }

  async logout(): Promise<void> {
    // signOutEverywhere clears the local Firebase session AND the centralized
    // IdP session (otherwise the auth guard silently re-logs the user in), then
    // navigates to the centralized login page — so nothing runs after it.
    await this.authBridge.bridge.signOutEverywhere();
  }

  private async ensureUserDocument(user: User): Promise<AppUser> {
    const db = getFirestore();
    const userRef = doc(db, 'users', user.uid);
    const snapshot = await getDoc(userRef);

    if (snapshot.exists()) {
      const data = snapshot.data();
      return {
        uid: user.uid,
        displayName: data['displayName'] ?? user.displayName ?? '',
        email: data['email'] ?? user.email ?? '',
        profilePictureUrl: data['profilePictureUrl'] ?? undefined,
        profilePictureStoragePath: data['profilePictureStoragePath'] ?? undefined,
        createdAt: data['createdAt']?.toDate() ?? new Date(),
      };
    }

    const appUser: AppUser = {
      uid: user.uid,
      displayName: user.displayName ?? '',
      email: user.email ?? '',
      createdAt: new Date(),
    };

    await setDoc(userRef, {
      displayName: appUser.displayName,
      email: appUser.email,
      createdAt: appUser.createdAt,
    });

    return appUser;
  }

  refreshCurrentUser(patch: Partial<AppUser>): void {
    const current = this._currentUser();
    if (!current) return;
    this._currentUser.set({ ...current, ...patch });
  }
}
